import { useCallback, useMemo } from 'react';
import { useHasPermission } from '@/entities/profile';
import {
  toSharedPresetListFields,
  useSharedPresets,
  useSharePreset,
  type SharedPreset,
  type SharedPresetTransfer,
  type SharedPresetUser,
} from '@/entities/shared-presets';
import { useCreateSystemPreset, useSystemPresets } from '@/entities/system-presets';
import { isQueryDataReceived } from '@/shared/api';
import { stableStringify } from '@/shared/lib';
import { findPreviousPreset, pickPresetSnapshot } from './store';
import {
  buildPresetSectionKey,
  buildSharedPresetId,
  buildSystemPresetId,
  getSectionTableName,
  isPresetKeyOfTable,
  parseSharedPresetId,
  parseSystemPresetId,
} from './tableKeys';
import { useServerPresetMutations } from './useServerPresetMutations';
import { useSettings } from './useSettings';
import {
  TABLE_PRESET_SNAPSHOT_KEYS,
  TableSettings,
  type TablePresetSnapshotKey,
  type TableSelectionDescription,
  type TableViewSnapshot,
} from './types';

/** Описание пресета для UI: id-ключ пресета, имя, снимок настроек и права на него. */
export type TablePreset = {
  /**
   * Полный ключ пресета: личного — в `tables` (`<tableName>:<uuid>`), системного — `<tableName>:system:<id>`,
   * совместного — `<tableName>:shared:<id>`
   */
  id: string;
  /** Пользовательское имя пресета */
  name: string;
  /** Источник внешней выборки, из которой создан пресет; `undefined` — пресет заведён вручную */
  selectionSource?: string;
  /** Пресет закреплён отдельным пунктом левого меню */
  pinned: boolean;
  /** Снимок настроек таблицы (только поля из TABLE_PRESET_SNAPSHOT_KEYS) */
  settings: TableSettings;
  /** Пресет системный — общий для всех пользователей */
  isSystem: boolean;
  /** Пресет совместный — им владелец поделился с выбранными пользователями */
  isShared: boolean;
  /** Владелец совместного пресета */
  owner?: SharedPresetUser;
  /** Получатели совместного пресета — известны только его владельцу */
  recipients?: SharedPresetUser[];
  /**
   * Текущий пользователь вправе править этот пресет: свой личный, системный при разрешении
   * либо совместный, которым владеет
   */
  isEditable: boolean;
};

/**
 * Стабильное сериализующее сравнение двух снимков пресета.
 * Используется для определения «грязного» состояния (текущее ≠ активный пресет).
 * Сравниваются только поля из {@link TABLE_PRESET_SNAPSHOT_KEYS}.
 */
const stringifySnapshot = (settings: TableSettings | undefined): string => {
  if (!settings) return '{}';
  const parts: string[] = [];
  for (const key of TABLE_PRESET_SNAPSHOT_KEYS) {
    parts.push(`${key}=${stableStringify(settings[key])}`);
  }
  return parts.join('|');
};

const byName = (a: TablePreset, b: TablePreset): number =>
  a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });

/**
 * Хук работы с именованными пресетами настроек таблицы.
 *
 * Личные пресеты хранятся в `settings.tables` под ключами `<tableName>:<uuid>` и отличаются
 * от рабочих записей наличием поля `name`. Системные и совместные живут на сервере и попадают
 * в тот же список. У системного личное только применение, а название, вид и закрепление общие
 * для всех; у совместного личные применение и закрепление, а название и вид общие для владельца
 * и получателей.
 * Список пресетов один на таблицу, а активный пресет и вид — свои у каждого раздела: они
 * читаются из рабочей записи `tables[sectionKey]`, и туда же пишут все экшены вида
 * (применение, вход в слой, снятие).
 *
 * Общий пресет меняется без ведома применившего, поэтому расхождение текущего вида с пресетом
 * разделено на два: донастройку пользователя (`isDirty`) и устаревание применённого вида
 * (`isOutdated`) — их различает отметка вида на момент применения (`activePresetView`).
 * Подхват нового вида пресета — {@link useActivePresetRefresh}.
 *
 * @param sectionKey - Ключ рабочей записи: имя таблицы (например, `'dialogsListV2'`)
 * либо ключ раздела закреплённого пресета.
 */
export const useTablePresets = (sectionKey: string) => {
  const tableName = getSectionTableName(sectionKey);
  const {
    userId,
    tables,
    setTableSettings,
    saveTablePreset,
    createTablePreset,
    updateTablePreset,
    applyTablePreset,
    enterTableView,
    deleteTablePreset,
    renameTablePreset,
    setTablePresetPinned,
    pruneServerPresetSections,
    clearActiveTablePreset,
    exitTableSelection,
  } = useSettings();

  const canManageSystemPresets = useHasPermission('ADMIN_SYSTEM_PRESET');
  const systemPresetsQuery = useSystemPresets(tableName);
  const systemPresetsData = systemPresetsQuery.data;
  const { mutateAsync: createSystemPreset, isPending: isMakingSystem } = useCreateSystemPreset();
  const sharedPresetsQuery = useSharedPresets(tableName);
  const sharedPresetsData = sharedPresetsQuery.data;
  const parseSystemId = useCallback((presetId: string) => parseSystemPresetId(presetId, tableName), [tableName]);
  const parseSharedId = useCallback((presetId: string) => parseSharedPresetId(presetId, tableName), [tableName]);
  const { updateServerPreset, pinServerPreset, deleteServerPreset } = useServerPresetMutations({
    parseSystemId,
    parseSharedId,
  });

  const systemPresets = useMemo<TablePreset[]>(() => {
    const list = (systemPresetsData?.items ?? []).map<TablePreset>((preset) => ({
      id: buildSystemPresetId(tableName, preset.id),
      name: preset.name,
      pinned: preset.isPinned,
      settings: preset.settings,
      isSystem: true,
      isShared: false,
      isEditable: canManageSystemPresets,
    }));
    list.sort(byName);
    return list;
  }, [systemPresetsData, tableName, canManageSystemPresets]);

  const sharedPresets = useMemo<TablePreset[]>(() => {
    const list = (sharedPresetsData?.items ?? []).map<TablePreset>((preset) => ({
      id: buildSharedPresetId(tableName, preset.id),
      ...toSharedPresetListFields(preset),
      settings: preset.settings,
      isSystem: false,
    }));
    list.sort(byName);
    return list;
  }, [sharedPresetsData, tableName]);

  const personalPresets = useMemo<TablePreset[]>(() => {
    const list: TablePreset[] = [];
    for (const [key, value] of Object.entries(tables)) {
      if (!isPresetKeyOfTable(key, tableName)) continue;
      if (!value.name) continue;
      list.push({
        id: key,
        name: value.name,
        selectionSource: value.selectionSource,
        pinned: value.pinnedToMenu === true,
        settings: value,
        isSystem: false,
        isShared: false,
        isEditable: true,
      });
    }
    list.sort(byName);
    return list;
  }, [tables, tableName]);

  // Системные идут первыми: это общий для всех набор, за ним — совместные, которыми поделились
  // пользователи, а личные пользователь заводит поверх них.
  const presets = useMemo<TablePreset[]>(
    () => [...systemPresets, ...sharedPresets, ...personalPresets],
    [systemPresets, sharedPresets, personalPresets],
  );

  // Набор пресетов известен целиком: личные лежат в настройках пользователя и известны сразу,
  // системные и совместные приходят с сервера отдельно. Пока их не получили, отсутствие пресета
  // в наборе ещё ничего не означает, и выводы «такого пресета нет» на нём строить нельзя — в том
  // числе после отказа запроса: окончание загрузки без ответа списка не даёт.
  // Раздел без пресетов запроса не делает — там набор известен сразу и он пуст.
  const isPresetListKnown =
    !tableName || (isQueryDataReceived(systemPresetsQuery) && isQueryDataReceived(sharedPresetsQuery));

  // Серверные пресеты в настройках пользователя не хранятся — их снимок берётся отсюда
  const serverPresetById = useMemo(
    () => new Map([...systemPresets, ...sharedPresets].map((preset) => [preset.id, preset])),
    [systemPresets, sharedPresets],
  );

  const activeRecord = tables[sectionKey];
  const activePresetId = activeRecord?.activePresetId;
  const previousPresetId = activeRecord?.previousPresetId;
  const activePresetView = activeRecord?.activePresetView;
  const activePreset = useMemo(() => presets.find((p) => p.id === activePresetId) ?? null, [presets, activePresetId]);

  // Пресет изменился после того, как был применён, а пользователь его вида не касался: применённый
  // вид устарел. Без отметки о применении (набор от прежней версии) устаревание неотличимо
  // от донастройки — считаем видом пользователя и молча ничего не подменяем.
  const isOutdated = useMemo(() => {
    if (!activePreset || !activePresetView) return false;
    const appliedView = stringifySnapshot(activePresetView);
    return stringifySnapshot(activeRecord) === appliedView && stringifySnapshot(activePreset.settings) !== appliedView;
  }, [activePreset, activePresetView, activeRecord]);

  // Донастройка — расхождение с видом пресета, сделанное самим пользователем. Расхождение,
  // пришедшее с правкой общего пресета, донастройкой не считается: предлагать пользователю
  // сохранить копию или записать в пресет чужой вид, которого он не выбирал, нельзя.
  const isDirty = useMemo(() => {
    if (!activePreset || isOutdated) return false;
    return stringifySnapshot(activeRecord) !== stringifySnapshot(activePreset.settings);
  }, [activePreset, activeRecord, isOutdated]);

  const savePresetAs = useCallback(
    (name: string, selection?: { source?: string; description?: TableSelectionDescription | null }): string =>
      saveTablePreset(userId, sectionKey, name, selection?.source, selection?.description ?? undefined),
    [saveTablePreset, userId, sectionKey],
  );

  // Отметка о виде, с которым пресет считается применённым: от неё отсчитывается донастройка
  // пользователя, поэтому обновляется везде, где вид таблицы сходится с видом пресета.
  const rememberAppliedView = useCallback(
    (view: TableSettings | undefined) => setTableSettings(sectionKey, { activePresetView: pickPresetSnapshot(view) }),
    [setTableSettings, sectionKey],
  );

  const saveCurrentToActivePreset = useCallback(() => {
    if (!activePresetId) return;
    const snapshot = pickPresetSnapshot(activeRecord);
    // Записанный вид становится точкой отсчёта только после ответа сервера: отказ по правам
    // оставляет донастройку пользователя при нём — вместе с предложением сохранить её копией.
    if (updateServerPreset(activePresetId, { settings: snapshot }, () => rememberAppliedView(snapshot))) return;
    updateTablePreset(userId, sectionKey, activePresetId);
  }, [updateTablePreset, updateServerPreset, rememberAppliedView, userId, sectionKey, activePresetId, activeRecord]);

  const apply = useCallback(
    (presetId: string) => applyTablePreset(userId, sectionKey, presetId, serverPresetById.get(presetId)?.settings),
    [applyTablePreset, userId, sectionKey, serverPresetById],
  );

  const enterView = useCallback(
    (view: TableViewSnapshot, options?: { backup?: boolean }) => enterTableView(userId, sectionKey, view, options),
    [enterTableView, userId, sectionKey],
  );

  const remove = useCallback(
    (presetId: string) => {
      // Серверный пресет пропадает из списка только с ответом сервера, поэтому и применение
      // снимается по ответу: отказ оставляет пресет применённым.
      const isServerPreset = deleteServerPreset(presetId, () => {
        if (activePresetId === presetId) clearActiveTablePreset(userId, sectionKey);
      });
      if (!isServerPreset) deleteTablePreset(userId, presetId);
    },
    [deleteTablePreset, deleteServerPreset, clearActiveTablePreset, userId, sectionKey, activePresetId],
  );

  const rename = useCallback(
    (presetId: string, name: string) => {
      if (!updateServerPreset(presetId, { name })) renameTablePreset(userId, presetId, name);
    },
    [renameTablePreset, updateServerPreset, userId],
  );

  const setPinned = useCallback(
    (presetId: string, pinned: boolean) => {
      if (!pinServerPreset(presetId, pinned)) setTablePresetPinned(userId, presetId, pinned);
    },
    [setTablePresetPinned, pinServerPreset, userId],
  );

  /**
   * Убирает разделы серверных пресетов, которых нет среди закреплённых в списке: пресет удалён,
   * возвращён в личные, откреплён или больше не доступен пользователю. Иначе вид, оставленный
   * в разделе, всплыл бы при новом закреплении того же пресета. Зовётся только с полученным
   * списком (`isPresetListKnown`): до него пропавшим выглядит любой серверный пресет.
   */
  const pruneServerSections = useCallback(() => {
    const pinnedServerPresetIds = new Set(
      [...systemPresets, ...sharedPresets].filter((preset) => preset.pinned).map((preset) => preset.id),
    );
    pruneServerPresetSections(userId, tableName, pinnedServerPresetIds);
  }, [pruneServerPresetSections, systemPresets, sharedPresets, userId, tableName]);

  // Ответ сервера возвращается вызывающему: пока он не пришёл, повторное нажатие завело бы второй
  // системный пресет из того же личного.
  const makeSystem = useCallback(
    async (presetId: string) => {
      const preset = personalPresets.find((item) => item.id === presetId);
      if (!preset) return;
      // Закрепление не переносится: у личного пресета оно личное, у системного — общее для всех.
      await createSystemPreset(
        { tableName, name: preset.name, settings: pickPresetSnapshot(preset.settings) },
        {
          onSuccess: (created) => {
            const wasActive = activePresetId === presetId;
            deleteTablePreset(userId, presetId);
            if (wasActive) {
              applyTablePreset(userId, sectionKey, buildSystemPresetId(tableName, created.id), created.settings);
            }
          },
        },
      );
    },
    [
      createSystemPreset,
      deleteTablePreset,
      applyTablePreset,
      personalPresets,
      userId,
      sectionKey,
      tableName,
      activePresetId,
    ],
  );

  const findPersonalTransfer = useCallback(
    (presetId: string): SharedPresetTransfer<TableViewSnapshot> | undefined => {
      const preset = personalPresets.find((item) => item.id === presetId);
      return preset && { name: preset.name, settings: pickPresetSnapshot(preset.settings), pinned: preset.pinned };
    },
    [personalPresets],
  );

  const findSharedTransfer = useCallback(
    (presetId: string): SharedPresetTransfer<TableViewSnapshot> | undefined => {
      const preset = sharedPresets.find((item) => item.id === presetId);
      return preset && { name: preset.name, settings: preset.settings, pinned: preset.pinned };
    },
    [sharedPresets],
  );

  // Раздел закреплённого пресета под новым ключом заводится заново и при первом заходе применяет
  // пресет сам — применять в прежний раздел незачем.
  const isAppliedInPlace = useCallback(
    (presetId: string, wasActive: boolean) => wasActive && sectionKey !== buildPresetSectionKey(presetId),
    [sectionKey],
  );

  const replaceWithShared = useCallback(
    (personalId: string, created: SharedPreset<TableViewSnapshot>, wasActive: boolean) => {
      deleteTablePreset(userId, personalId);
      if (isAppliedInPlace(personalId, wasActive)) {
        applyTablePreset(userId, sectionKey, buildSharedPresetId(tableName, created.id), created.settings);
      }
    },
    [deleteTablePreset, applyTablePreset, isAppliedInPlace, userId, sectionKey, tableName],
  );

  const replaceWithPersonal = useCallback(
    (sharedPresetId: string, preset: SharedPresetTransfer<TableViewSnapshot>, wasActive: boolean) => {
      const personalId = createTablePreset(userId, tableName, preset.name, preset.settings, preset.pinned);
      if (isAppliedInPlace(sharedPresetId, wasActive)) applyTablePreset(userId, sectionKey, personalId);
    },
    [createTablePreset, applyTablePreset, isAppliedInPlace, userId, sectionKey, tableName],
  );

  const { share, isSharing } = useSharePreset<TableViewSnapshot>({
    tableName,
    activePresetId,
    parseSharedId,
    findPersonal: findPersonalTransfer,
    findShared: findSharedTransfer,
    replaceWithShared,
    replaceWithPersonal,
  });

  const clearActive = useCallback(
    () => clearActiveTablePreset(userId, sectionKey),
    [clearActiveTablePreset, userId, sectionKey],
  );

  // Снимок серверного пресета, поверх которого открыта выборка: в настройках пользователя его нет,
  // и без снимка снятие выборки вернуло бы личный вид вместо вида пресета.
  const previousServerPresetSnapshot = useMemo(
    () => (previousPresetId ? serverPresetById.get(previousPresetId)?.settings : undefined),
    [previousPresetId, serverPresetById],
  );

  // Вид, поверх которого ложится следующая внешняя выборка. Из личного вида и из пресета это текущий
  // вид раздела — его вход в слой и отложит. В уже открытом слое выборки — тот, к которому вернёт её
  // снятие: вид пресета под выборкой либо отложенный личный вид. Иначе каждый следующий переход
  // ложился бы поверх вида прежней выборки и копил бы её колонки.
  const selectionBaseView = useMemo<TableViewSnapshot>(() => {
    if (activeRecord && activePresetId === undefined) {
      const previousPreset = findPreviousPreset(tables, activeRecord, previousServerPresetSnapshot);
      const layerBase = previousPreset ? pickPresetSnapshot(previousPreset) : activeRecord.previousView;
      if (layerBase) return layerBase;
    }
    return pickPresetSnapshot(activeRecord);
  }, [tables, activeRecord, activePresetId, previousServerPresetSnapshot]);

  const exitSelection = useCallback(
    (selectionFilterId: string) =>
      exitTableSelection(userId, sectionKey, selectionFilterId, previousServerPresetSnapshot),
    [exitTableSelection, userId, sectionKey, previousServerPresetSnapshot],
  );

  return {
    presets,
    isPresetListKnown,
    activePresetId,
    activePreset,
    activePresetView,
    isDirty,
    isOutdated,
    canManageSystemPresets,
    isSharing,
    rememberAppliedView,
    savePresetAs,
    saveCurrentToActivePreset,
    apply,
    enterView,
    selectionBaseView,
    remove,
    rename,
    setPinned,
    pruneServerSections,
    makeSystem,
    isMakingSystem,
    share,
    clearActive,
    exitSelection,
  };
};

// Чтобы тип TablePresetSnapshotKey не выпал при анализе использований
export type { TablePresetSnapshotKey };
